export interface StockChecklistItem {
  id: string;
  checklist_id: string;
  product_id: string;
  company_id: string;
  sku?: string;
  name: string;
  ean?: string;
  category?: string;
  current_stock: number;
  counted_stock?: number;
  difference?: number;
  checked: boolean;
  notes?: string;
  checked_at?: string;
  checked_by?: string;
  unit_cost?: number;
  sale_price?: number;
  deposit_name?: string;
  adjustment_type?: 'entrada' | 'saida' | 'nao_acertado_esperado_negativo' | 'nao_acertado_cadastro_diverge' | null;
  unit_name?: string;
  price_table_name?: string;
  system_sale_price?: number;
  system_cost_price?: number;
  image_url?: string;
  temp_image_url?: string;
  // A2 price-edit tracking
  price_edited_by_user?: boolean;
  cost_edited_by_user?: boolean;
  // B2 per price-table overrides  { [price_table_id]: new_sale_table_price }
  price_table_overrides?: Record<string, number>;
  /** Current prices per table from product_prices — { [price_table_id]: current_sale_table_price } */
  current_prices_by_table?: Record<string, number>;
  // B3 photo tracking
  photo_updated_by?: string;
  photo_updated_at?: string;
  // A7a-i (I.159): conferência por depósito; o acerto é contado − (saldo − com cliente) da hora da contagem
  product_type?: string;
  deposit_stock_now?: number;
  customer_out_now?: number | null;
  registered_now?: number | null;
  expected_on_shelf_now?: number;
  system_stock_at_count?: number | null;
  customer_out_at_count?: number | null;
  registered_units_at_count?: number | null;
  expected_on_shelf_at_count?: number | null;
  skip_reason?: StockCheckSkipReason | null;
  // A8-i (I.160): origem do custo sugerido/definido (D-A8.2); nulo = conferência de antes do A8-i
  cost_origin?: StockCostOrigin | null;
  cost_defined_at?: string | null;
  // A7b (I.160): quem contou, item achado na prateleira e m-CONF.5 (movimentos da finalização)
  checked_by_name?: string | null;
  added_at?: string | null;
  added_by_name?: string | null;
  cost_adjusted?: boolean;
  applied_cost?: number | null;
  average_cost_after_completion?: number | null;
}

/** A7b: porta da conferência — chave (master ou stock:conference), senha (entrou pela senha), pedir_senha, sem_permissao. */
export type StockCheckAccessMode = 'chave' | 'senha' | 'pedir_senha' | 'sem_permissao';

/** A7b: resposta de get_stock_checklist_count_page (no modo senha os itens não trazem preço, custo nem tabela). */
export interface StockCheckCountPage {
  success: boolean;
  error?: string;
  mode: 'chave' | 'senha';
  can_edit_price: boolean;
  can_edit_cost: boolean;
  checklist: {
    id: string;
    name: string;
    status: string;
    deposit_id?: string | null;
    balance_id?: string | null;
    password_required: boolean;
  };
  totals: { items: number; counted: number; pending: number; divergent: number };
  filtered_total: number;
  page: number;
  page_size: number;
  categories: { category: string | null; items: number }[];
  items: StockChecklistItem[];
}

/** A8-i: de onde veio o custo do item (ordem informado > último custo > custo médio > cadastrado > sem custo). */
export type StockCostOrigin =
  | 'informado'
  | 'ultimo_custo'
  | 'custo_medio'
  | 'cadastrado'
  | 'cadastrado_confirmado'
  | 'sem_custo';

export const COST_ORIGIN_LABEL: Record<StockCostOrigin, string> = {
  informado: 'informado',
  ultimo_custo: 'último custo',
  custo_medio: 'custo médio',
  cadastrado: 'cadastrado — confirmar',
  cadastrado_confirmado: 'cadastrado confirmado',
  sem_custo: 'sem custo',
};

/**
 * A7b (m-CONF.5): colunas do PDF que vêm da leitura da conferência (get_stock_checklist_by_token), iguais nas duas
 * montagens do PDF. Custo aplicado e custo médio nulos = "sem mudança" no PDF.
 */
export function stockChecklistPdfTrail(i: StockChecklistItem) {
  return {
    counted_by_name:    i.checked_by_name ?? null,
    found_on_shelf:     !!i.added_at,
    cost_origin_label:  i.cost_origin ? COST_ORIGIN_LABEL[i.cost_origin] : null,
    applied_cost:       i.cost_adjusted ? i.applied_cost ?? null : null,
    average_cost_after: i.average_cost_after_completion ?? null,
  };
}

/** A8-i: balanço ao qual a conferência pertence (parte). */
export interface StockBalanceRef {
  id: string;
  name: string;
  status: 'open' | 'decreed' | 'cancelled';
  counts_valid_since: string;
}

/** A7a-i: motivo de um item contado não ser acertado na finalização (regra única do banco). */
export type StockCheckSkipReason = 'esperado_negativo' | 'cadastro_diverge';

export interface StockCheckSkippedItem {
  product_id: string;
  name: string;
  reason: StockCheckSkipReason;
  system: number;
  customer_out: number | null;
  expected: number;
  registered: number | null;
  counted: number;
}

export interface StockCheckCadastroAviso {
  product_id: string;
  name: string;
  counted: number;
  registered: number;
}

export const SKIP_REASON_LABEL: Record<StockCheckSkipReason, string> = {
  esperado_negativo: 'conferir contratos',
  cadastro_diverge: 'cadastro de unidades diverge',
};

/**
 * Column visibility config for the counting table.
 * Fixed columns (Status, SKU, Nome, Estoque, Contagem, Dif.) are always shown
 * and are NOT part of this config.
 * Optional columns with edit pair show two columns each (current | new).
 */
export interface ChecklistColumnConfig {
  /** Unidade de Medida — readonly informative */
  unit: boolean;
  /** Categoria — readonly informative */
  category: boolean;
  /** Código de Barras — readonly informative */
  ean: boolean;
  /** Preço de Venda — dual: current (readonly) + new (editable) */
  sale_price: boolean;
  /** Preço de Custo — dual: current (readonly) + new (editable) */
  cost_price: boolean;
  /** Foto do Produto — thumbnail + upload */
  photo: boolean;
  /**
   * Selected price tables for per-table dual columns.
   * Each entry stores id + name at creation time so names don't require a join.
   * Each selected table adds 2 columns: "Tabela X Atual" (readonly) + "Tabela X Novo" (editable).
   */
  price_tables?: Array<{ id: string; name: string }>;
}

export interface ChecklistFilters {
  categories?: string[];
  suppliers?: string[];
  warehouses?: string[];
  brands?: string[];
  locations?: string[];
  abc_curves?: ('A' | 'B' | 'C')[];
  letterFilter?: string[];
  specificProductIds?: string[];
  onlyMinStock?: boolean;
  onlyInactive?: boolean;
  onlyZeroNegative?: boolean;
  /** A8-i: só produtos não contados neste depósito desde a data (AAAA-MM-DD, dia de São Paulo) */
  notCountedSince?: string;
  visible_columns?: ChecklistColumnConfig;
}

export interface StockChecklist {
  id: string;
  company_id: string;
  name: string;
  description?: string;
  filters: ChecklistFilters;
  status: 'draft' | 'in_progress' | 'completed' | 'cancelled';
  created_at: string;
  created_by: string;
  completed_at?: string;
  completed_by?: string;
  access_token?: string;
  deposit_id?: string;
  /** A8-i: parte de balanço */
  balance_id?: string | null;
  items?: StockChecklistItem[];
}

export interface ChecklistFormData {
  name: string;
  description?: string;
  filters: ChecklistFilters;
  deposit_id?: string;
  specificProductIds?: string[];
}

export interface PublicChecklistData {
  checklist: {
    id: string;
    name: string;
    description?: string;
    status: string;
    deposit_id?: string;
    balance_id?: string | null;
    created_at: string;
    filters?: ChecklistFilters;
  };
  company: {
    name: string;
    logo_url?: string;
  };
  deposit?: {
    id: string;
    name: string;
    is_main?: boolean;
  };
  // A8-i: balanço da parte, quem pode informar custo (master ou "Gerenciar compras") e se é master
  balance?: StockBalanceRef | null;
  can_edit_cost?: boolean;
  is_master?: boolean;
  items: StockChecklistItem[];
}

/** A7b: dados da página de contagem fora da página do banco (lidos como na lista, filtrados pela empresa). */
export interface StockCheckPageHeader {
  created_at: string;
  filters?: ChecklistFilters;
  deposit_name?: string | null;
  balance?: StockBalanceRef | null;
}

export const DEFAULT_COLUMN_CONFIG: ChecklistColumnConfig = {
  unit: true,
  category: true,
  ean: false,
  sale_price: true,
  cost_price: true,
  photo: false,
};
